/**
 * LaserStream - Energy transmission beams
 * FIXED: Proper targeting to GEO Receiver, not through Earth
 * FIXED: Depth testing for proper occlusion
 * FIXED: Smooth gradients without artifacts
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';
import { LASER_RELAY_NETWORK } from '../../data/scientificData';

// === PRE-ALLOCATED VECTORS FOR PERFORMANCE ===
const _particleDirection = new THREE.Vector3();
const _particlePoint = new THREE.Vector3();

interface LaserBeamProps {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  coreColor?: string;
  thickness?: number;  // Base thickness multiplier (default 1.0)
}

// STABLE LASER BEAM - Smooth gradient, proper depth testing
// THIN beams for realistic laser appearance
function LaserBeam({ start, end, color, coreColor, thickness = 1.0 }: LaserBeamProps) {
  // Create tube geometries ONCE - stable, no animation, smooth
  // Visible laser beams for main transmission lines
  const { tubeGeometry, midGlowGeometry, outerGlowGeometry } = useMemo(() => {
    const path = new THREE.LineCurve3(start.clone(), end.clone());
    // Visible laser beam radii (main transmission beams)
    const coreRadius = 0.025 * thickness;
    const midRadius = 0.05 * thickness;
    const outerRadius = 0.09 * thickness;
    // Higher segment count for smoother appearance
    const tube = new THREE.TubeGeometry(path, 128, coreRadius, 8, false);
    const midGlow = new THREE.TubeGeometry(path, 128, midRadius, 8, false);
    const outerGlow = new THREE.TubeGeometry(path, 128, outerRadius, 8, false);
    return { tubeGeometry: tube, midGlowGeometry: midGlow, outerGlowGeometry: outerGlow };
  }, [start, end, thickness]);

  return (
    <group>
      {/* Outer glow - soft halo, WITH depth test for occlusion */}
      <mesh geometry={outerGlowGeometry}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.15}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>

      {/* Middle glow - WITH depth test */}
      <mesh geometry={midGlowGeometry}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.4}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>

      {/* Core beam - BRIGHT, STABLE, WITH depth test */}
      <mesh geometry={tubeGeometry}>
        <meshBasicMaterial
          color={coreColor || "#ffffff"}
          transparent
          opacity={1}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

// Energy particles flowing along beam
function LaserParticles({
  start,
  end,
  color,
  count = 50
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: number;
  count?: number;
}) {
  const pointsRef = useRef<THREE.Points>(null);
  const progressRef = useRef<Float32Array>(new Float32Array(count).fill(0).map(() => Math.random()));

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const direction = end.clone().sub(start);

    for (let i = 0; i < count; i++) {
      const t = progressRef.current[i];
      const point = start.clone().add(direction.clone().multiplyScalar(t));
      pos[i * 3] = point.x;
      pos[i * 3 + 1] = point.y;
      pos[i * 3 + 2] = point.z;
    }

    return pos;
  }, [start, end, count]);

  useFrame((state, delta) => {
    if (!pointsRef.current) return;

    const pos = pointsRef.current.geometry.attributes.position.array as Float32Array;
    // PERFORMANCE: Use pre-allocated vector and compute direction once
    _particleDirection.subVectors(end, start);

    for (let i = 0; i < count; i++) {
      progressRef.current[i] += delta * (0.3 + Math.random() * 0.1);

      if (progressRef.current[i] > 1) {
        progressRef.current[i] = 0;
      }

      const t = progressRef.current[i];
      // PERFORMANCE: Use pre-allocated vectors instead of clone()
      _particlePoint.copy(start).addScaledVector(_particleDirection, t);
      // Small jitter for visible beam
      pos[i * 3] = _particlePoint.x + (Math.random() - 0.5) * 0.02;
      pos[i * 3 + 1] = _particlePoint.y + (Math.random() - 0.5) * 0.02;
      pos[i * 3 + 2] = _particlePoint.z + (Math.random() - 0.5) * 0.02;
    }

    pointsRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.06}
        color={color}
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        depthTest={true}
        toneMapped={false}
        sizeAttenuation={true}
      />
    </points>
  );
}

interface LaserStreamProps {
  sunPosition?: [number, number, number];
  mercuryPosition?: [number, number, number];
  earthPosition?: [number, number, number];
  geoReceiverPosition?: [number, number, number];
  eahpBasePosition?: [number, number, number];
}

export function LaserStream({
  sunPosition = [-30, 0, 0],
  mercuryPosition = [-2, 0, 15],  // 0.4 AU from sun
  earthPosition = [50, 0, 0],      // 1.0 AU from sun
  geoReceiverPosition, // Will be calculated if not provided
  eahpBasePosition = [-24.4, 1, 5.6]  // EAHP Construction Base position
}: LaserStreamProps) {
  const { view } = useStore();

  if (!view.showLasers) return null;

  // Mercury center position
  const mercuryCenter = new THREE.Vector3(...mercuryPosition);
  const sunCenter = new THREE.Vector3(...sunPosition);

  // Mercury Orbital Relay Hub - positioned TOWARD the Sun (inner orbit side)
  // This ensures clear LOS from EAHP without passing through Mercury
  const sunToMercury = mercuryCenter.clone().sub(sunCenter).normalize();
  const mercuryRelayPoint = mercuryCenter.clone().sub(sunToMercury.clone().multiplyScalar(3.5));
  mercuryRelayPoint.y += 0.5; // Slight vertical offset for visibility

  // EAHP emission point - from the ACTUAL EAHP Construction Base!
  // Laser originates from the base where satellites are assembled
  const eahpPoint = new THREE.Vector3(...eahpBasePosition);

  // GEO Receiver position - LOS-OPTIMIZED orbital placement
  // Clear line-of-sight from Mercury, beam passes well clear of Earth
  const geoReceiverPoint = geoReceiverPosition
    ? new THREE.Vector3(...geoReceiverPosition)
    : new THREE.Vector3(earthPosition[0] + 4, 3, 5); // Clear LOS position

  return (
    <group>
      {/* BEAM 1: EAHP Swarm → Mercury Relay - HOT PINK-ORANGE */}
      <LaserBeam
        start={eahpPoint}
        end={mercuryRelayPoint}
        color="#ff0055"
        coreColor="#ffaa00"
      />
      <LaserParticles
        start={eahpPoint}
        end={mercuryRelayPoint}
        color={0xff3366}
        count={60}
      />

      {/* BEAM 2: Mercury Relay → GEO Receiver - HOT ORANGE-YELLOW */}
      <LaserBeam
        start={mercuryRelayPoint}
        end={geoReceiverPoint}
        color="#ff6600"
        coreColor="#ffcc00"
      />
      <LaserParticles
        start={mercuryRelayPoint}
        end={geoReceiverPoint}
        color={0xff9900}
        count={50}
      />

      {/* NO BEAM TO EARTH CENTER - The GEO Receiver is the FINAL DESTINATION */}
      {/* Power is transmitted to Earth via microwave to the ground station */}

      {/* Microwave downlink - targets the SpaceX ground station */}
      <MicrowaveDownlink
        from={geoReceiverPoint}
        to={new THREE.Vector3(
          earthPosition[0] + 1.0,  // Ground station X offset
          0.25,                     // Ground station Y offset
          0.5                       // Ground station Z offset
        )}
      />

      {/* Relay node markers */}
      <RelayNode position={mercuryRelayPoint} label="Mercury Relay" color={0xff6600} />
      <RelayNode position={geoReceiverPoint} label="GEO Receiver" color={0x00ffcc} />
    </group>
  );
}

// Microwave downlink visualization (thin but visible)
function MicrowaveDownlink({ from, to }: { from: THREE.Vector3; to: THREE.Vector3 }) {
  // Create thin beam representing microwave transmission to Earth
  const path = useMemo(() => new THREE.LineCurve3(from.clone(), to.clone()), [from, to]);

  const { thinBeam, glowBeam } = useMemo(() => {
    return {
      thinBeam: new THREE.TubeGeometry(path, 64, 0.015, 8, false),
      glowBeam: new THREE.TubeGeometry(path, 64, 0.04, 8, false)
    };
  }, [path]);

  return (
    <group>
      {/* Outer glow */}
      <mesh geometry={glowBeam}>
        <meshBasicMaterial
          color={0x88ccff}
          transparent
          opacity={0.25}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>

      {/* Core beam - bright */}
      <mesh geometry={thinBeam}>
        <meshBasicMaterial
          color={0xaaddff}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

// Relay node visualization - with proper depth testing
function RelayNode({
  position,
  label,
  color
}: {
  position: THREE.Vector3;
  label: string;
  color: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.5;
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.2;
    }
  });

  return (
    <group position={position}>
      {/* Solid core - HIGH EMISSIVE */}
      <mesh ref={meshRef}>
        <icosahedronGeometry args={[0.15, 1]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={3}
          metalness={1}
          roughness={0.1}
          toneMapped={false}
        />
      </mesh>

      {/* Inner glow sphere - with depth test */}
      <mesh>
        <sphereGeometry args={[0.25, 16, 16]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.25}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>

      {/* Outer wireframe - with depth test */}
      <mesh rotation={[0, Math.PI / 4, 0]}>
        <octahedronGeometry args={[0.4, 0]} />
        <meshBasicMaterial
          color={color}
          wireframe
          transparent
          opacity={0.5}
          depthTest={true}
          toneMapped={false}
        />
      </mesh>

      {/* Point light for bloom */}
      <pointLight color={color} intensity={3} distance={10} />
    </group>
  );
}

// Export relay network data
export function useLaserRelayData() {
  return LASER_RELAY_NETWORK;
}
